import { useState } from "react";
import { Camera, FileText, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

/* Reusable photo/document gallery for forms: upload + per-item editable title/tag and date.
   items: [{ id, url (api-relative), title, date, content_type }]
   onUpload(file) -> Promise; onEdit(id, {title?|date?}) -> Promise; onDelete(id) -> Promise */
export default function PhotoAttach({ items = [], onUpload, onEdit, onDelete,
  accept = "image/jpeg,image/png,image/webp,image/heic,image/heif", label = "Add photo", testPrefix = "photo" }) {
  const [busy, setBusy] = useState(false);

  const src = (a) => `${API}${a.url.replace(/^\/api/, "")}?auth=${localStorage.getItem("token")}`;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB"); return; }
    setBusy(true);
    try { await onUpload(file); toast.success("Uploaded — add a title and date below"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveMeta = async (id, patch) => {
    try { await onEdit(id, patch); } catch { toast.error("Could not save"); }
  };

  return (
    <div data-testid={`${testPrefix}-gallery`}>
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
          {items.map((a) => (
            <div key={a.id} className="relative group border border-zinc-200 bg-white p-1.5" data-testid={`${testPrefix}-item-${a.id}`}>
              {a.content_type?.includes("pdf")
                ? <a href={src(a)} target="_blank" rel="noreferrer" className="w-full h-20 border border-zinc-100 bg-zinc-50 flex items-center justify-center text-primary"><FileText className="w-7 h-7" /></a>
                : <a href={src(a)} target="_blank" rel="noreferrer"><img src={src(a)} alt={a.title || ""} loading="lazy" className="w-full h-20 object-cover" /></a>}
              <input defaultValue={a.title || ""} placeholder="Title / tag"
                onBlur={(e) => { if ((e.target.value || "") !== (a.title || "")) saveMeta(a.id, { title: e.target.value }); }}
                data-testid={`${testPrefix}-title-${a.id}`}
                className="mt-1 w-full border border-zinc-200 px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-primary" />
              <input type="date" defaultValue={a.date || ""}
                onChange={(e) => saveMeta(a.id, { date: e.target.value })}
                data-testid={`${testPrefix}-date-${a.id}`}
                className="mt-1 w-full border border-zinc-200 px-1.5 py-0.5 text-xs outline-none focus:ring-1 focus:ring-primary" />
              <button onClick={() => onDelete(a.id)} data-testid={`${testPrefix}-delete-${a.id}`}
                className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <label data-testid={`${testPrefix}-add-btn`}
        className={`inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-primary text-primary ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}>
        {busy ? <Upload className="w-3.5 h-3.5 animate-pulse" /> : <Camera className="w-3.5 h-3.5" />} {busy ? "Uploading…" : label}
        <input type="file" accept={accept} onChange={upload} className="sr-only" data-testid={`${testPrefix}-input`} disabled={busy} />
      </label>
    </div>
  );
}
